import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    LinearProgress,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';

const fallbackTranslate = (text, replacements = {}) => {
    let output = text;
    Object.entries(replacements).forEach(([key, value]) => {
        output = output.replace(new RegExp(key, 'g'), value);
    });
    return output;
};

const translate = (text, replacements = {}) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text, replacements);
        } catch (error) {
            // ignore translation errors and fall back
        }
    }

    return fallbackTranslate(text, replacements);
};

const t = translate;

const defaultMeta = {
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
    search: '',
};

const resolveEndpoint = (template, streamId) => {
    if (!template) {
        return '';
    }
    return template.replace('__STREAM_ID__', streamId);
};

const formatUrlPreview = (url) => {
    if (!url) {
        return '';
    }
    if (url.length <= 60) {
        return url;
    }
    return `${url.slice(0, 40)}…${url.slice(-15)}`;
};

const StreamManagementApp = ({
    initialStreams = [],
    initialMeta = defaultMeta,
    endpoints = {},
    csrfToken = '',
}) => {
    const [streams, setStreams] = useState(Array.isArray(initialStreams) ? initialStreams : []);
    const [meta, setMeta] = useState({ ...defaultMeta, ...initialMeta });
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState(initialMeta.search || '');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [feedback, setFeedback] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogBusy, setDialogBusy] = useState(false);
    const [formValues, setFormValues] = useState({ name: '', url: '' });
    const [formErrors, setFormErrors] = useState({});
    const [importBusy, setImportBusy] = useState(false);
    const [importSummary, setImportSummary] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const searchDebounceRef = useRef(null);
    const initialisedRef = useRef(false);
    const fileInputRef = useRef(null);

    const perPage = meta.per_page || 25;

    const showFeedback = useCallback((message, severity = 'success') => {
        setFeedback({ message, severity });
    }, []);

    const hideFeedback = useCallback(() => {
        setFeedback(null);
    }, []);

    const apiFetch = useCallback(async (url, options = {}) => {
        const config = {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(options.body instanceof FormData
                    ? {}
                    : { 'Content-Type': 'application/json' }),
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...options.headers,
            },
            credentials: 'same-origin',
            ...options,
        };

        const response = await fetch(url, config);
        if (!response.ok) {
            let errorPayload = {};
            try {
                errorPayload = await response.json();
            } catch (parseError) {
                // ignore JSON parse error
            }

            const error = new Error(errorPayload.message || response.statusText || 'Request failed');
            error.status = response.status;
            error.payload = errorPayload;
            throw error;
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
            return response.json();
        }
        return response;
    }, [csrfToken]);

    const fetchStreams = useCallback(async ({ page = 1, search = '' } = {}) => {
        if (!endpoints.list) {
            return;
        }

        setLoading(true);
        try {
            const url = new URL(endpoints.list, window.location.origin);
            url.searchParams.set('page', page);
            url.searchParams.set('per_page', perPage);
            if (search) {
                url.searchParams.set('search', search);
            }

            const payload = await apiFetch(url.toString());
            const receivedData = Array.isArray(payload.data) ? payload.data : [];
            const receivedMeta = { ...defaultMeta, ...(payload.meta || {}) };

            if (receivedData.length === 0 && receivedMeta.total > 0 && page > 1 && page > receivedMeta.last_page) {
                return fetchStreams({ page: receivedMeta.last_page || 1, search });
            }

            setStreams(receivedData);
            setMeta(receivedMeta);
            setSelectedIds(new Set());
        } catch (error) {
            console.error('[StreamManagement] Failed to fetch streams', error);
            showFeedback(error.message || 'Failed to load streams.', 'error');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, endpoints.list, perPage, showFeedback]);

    const debouncedSearchUpdate = useCallback((nextValue) => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            const trimmed = nextValue.trim();
            setMeta((current) => ({ ...current, search: trimmed }));
            fetchStreams({ page: 1, search: trimmed });
        }, 400);
    }, [fetchStreams]);

    const handleSearchChange = useCallback((event) => {
        const value = event.target.value;
        setSearchInput(value);
        debouncedSearchUpdate(value);
    }, [debouncedSearchUpdate]);

    useEffect(() => {
        if (!initialisedRef.current) {
            initialisedRef.current = true;
            return undefined;
        }

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const handlePageChange = useCallback((event, value) => {
        const targetPage = Number(value) || 1;
        setMeta((current) => ({ ...current, current_page: targetPage }));
        fetchStreams({ page: targetPage, search: meta.search || '' });
    }, [fetchStreams, meta.search]);

    const toggleSelect = useCallback((streamId) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(streamId)) {
                next.delete(streamId);
            } else {
                next.add(streamId);
            }
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((current) => {
            if (current.size === streams.length) {
                return new Set();
            }
            return new Set(streams.map((stream) => stream.id));
        });
    }, [streams]);

    const openCreateDialog = useCallback(() => {
        setDialogMode('create');
        setFormValues({ name: '', url: '' });
        setFormErrors({});
        setDialogOpen(true);
        setEditingId(null);
    }, []);

    const openEditDialog = useCallback((stream) => {
        setDialogMode('edit');
        setFormValues({ name: stream.name || '', url: stream.url || '' });
        setFormErrors({});
        setDialogOpen(true);
        setEditingId(stream.id);
    }, []);

    const closeDialog = useCallback(() => {
        if (dialogBusy) {
            return;
        }
        setDialogOpen(false);
        setFormErrors({});
        setEditingId(null);
    }, [dialogBusy]);

    const handleFormChange = useCallback((field) => (event) => {
        const value = event.target.value;
        setFormValues((current) => ({ ...current, [field]: value }));
    }, []);

    const handleFormSubmit = useCallback(async () => {
        if (!endpoints.create) {
            return;
        }

        const payload = {
            name: formValues.name.trim(),
            url: formValues.url.trim(),
        };

        if (!payload.name) {
            setFormErrors({ name: translate('Name is required.') });
            return;
        }
        if (!payload.url) {
            setFormErrors({ url: translate('URL is required.') });
            return;
        }

        setDialogBusy(true);
        setFormErrors({});

        try {
            let response;
            if (dialogMode === 'create') {
                response = await apiFetch(endpoints.create, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                showFeedback(translate('Stream created successfully.'));
            } else {
                if (!editingId) {
                    throw new Error('Missing stream identifier for edit.');
                }
                const updateUrl = resolveEndpoint(endpoints.update, editingId);
                response = await apiFetch(updateUrl, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
                showFeedback(translate('Stream updated successfully.'));
            }

            if (response?.data) {
                const updatedStream = response.data;
                setStreams((current) => {
                    const exists = current.some((stream) => stream.id === updatedStream.id);
                    if (exists) {
                        return current.map((stream) => (stream.id === updatedStream.id ? updatedStream : stream));
                    }
                    const next = [updatedStream, ...current];
                    return next.slice(0, perPage);
                });
                setMeta((current) => ({ ...current, total: dialogMode === 'create' ? current.total + 1 : current.total }));
            }

            closeDialog();
            fetchStreams({ page: dialogMode === 'create' ? 1 : meta.current_page, search: meta.search || '' });
        } catch (error) {
            if (error?.payload?.errors) {
                const errors = error.payload.errors;
                setFormErrors(Object.fromEntries(Object.entries(errors).map(([key, messages]) => [key, messages[0] || ''])));
            } else {
                showFeedback(error.message || 'Failed to save stream.', 'error');
            }
        } finally {
            setDialogBusy(false);
        }
    }, [apiFetch, closeDialog, dialogMode, endpoints.create, endpoints.update, fetchStreams, formValues.name, formValues.url, meta.current_page, meta.search, perPage, showFeedback, editingId]);

    const handleDelete = useCallback(async (streamId) => {
        if (!endpoints.delete) {
            return;
        }
    const confirmed = window.confirm(translate('Are you sure you want to delete this stream?'));
        if (!confirmed) {
            return;
        }

        try {
            const url = resolveEndpoint(endpoints.delete, streamId);
            await apiFetch(url, { method: 'DELETE' });
            showFeedback(translate('Stream deleted.'));
            fetchStreams({ page: meta.current_page, search: meta.search || '' });
        } catch (error) {
            showFeedback(error.message || 'Failed to delete stream.', 'error');
        }
    }, [apiFetch, endpoints.delete, fetchStreams, meta.current_page, meta.search, showFeedback]);

    const handleBulkDelete = useCallback(async () => {
        if (!endpoints.bulkDelete || selectedIds.size === 0) {
            return;
        }
    const confirmed = window.confirm(translate('Delete selected streams? This cannot be undone.'));
        if (!confirmed) {
            return;
        }

        try {
            await apiFetch(endpoints.bulkDelete, {
                method: 'POST',
                body: JSON.stringify({ ids: Array.from(selectedIds) }),
            });
            showFeedback(translate('Selected streams deleted.'));
            fetchStreams({ page: meta.current_page, search: meta.search || '' });
        } catch (error) {
            showFeedback(error.message || 'Failed to delete selected streams.', 'error');
        }
    }, [apiFetch, endpoints.bulkDelete, fetchStreams, meta.current_page, meta.search, selectedIds, showFeedback]);

    const handleImportClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file || !endpoints.import) {
            return;
        }

        setImportBusy(true);
        setImportSummary(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiFetch(endpoints.import, {
                method: 'POST',
                body: formData,
            });

            if (response?.data) {
                setImportSummary(response.data);
                const summaryParts = [];
                if (response.data.created) summaryParts.push(translate('Created: :count', { ':count': response.data.created }));
                if (response.data.updated) summaryParts.push(translate('Updated: :count', { ':count': response.data.updated }));
                if (response.data.skipped) summaryParts.push(translate('Skipped: :count', { ':count': response.data.skipped }));
                if (response.data.skipped_extra_columns) summaryParts.push(translate('Ignored (extra columns): :count', { ':count': response.data.skipped_extra_columns }));
                showFeedback(summaryParts.join(' · ') || translate('CSV processed.'));
            } else {
                showFeedback(translate('CSV processed.'), 'info');
            }

            fetchStreams({ page: 1, search: meta.search || '' });
        } catch (error) {
            showFeedback(error.message || 'Failed to import CSV.', 'error');
        } finally {
            setImportBusy(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [apiFetch, endpoints.import, fetchStreams, meta.search, showFeedback]);

    const allSelected = useMemo(() => streams.length > 0 && selectedIds.size === streams.length, [selectedIds.size, streams.length]);
    const hasSelection = selectedIds.size > 0;

    const renderTableBody = () => {
        if (!loading && streams.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={6}>
                        <Box py={4} textAlign="center">
                            <Typography variant="body1" color="text.secondary">
                                {meta.search ? translate('No streams match your search.') : translate('No streams yet. Add your first stream to begin.')}
                            </Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }

        return streams.map((stream) => {
            const isSelected = selectedIds.has(stream.id);
            return (
                <TableRow key={stream.id} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isSelected}
                            onChange={() => toggleSelect(stream.id)}
                            inputProps={{ 'aria-label': translate('Select stream') }}
                        />
                    </TableCell>
                    <TableCell component="th" scope="row">
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <LinkIcon fontSize="small" color="action" />
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} title={stream.name}>
                                    {stream.name || translate('Untitled stream')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" title={stream.url}>
                                    {formatUrlPreview(stream.url)}
                                </Typography>
                            </Box>
                        </Stack>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" color="text.secondary">
                            {stream.last_used_at ? new Date(stream.last_used_at).toLocaleString() : translate('Never used')}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" color="text.secondary">
                            {stream.updated_at ? new Date(stream.updated_at).toLocaleString() : '—'}
                        </Typography>
                    </TableCell>
                    <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title={translate('Edit')}>
                                <IconButton size="small" onClick={() => openEditDialog(stream)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={translate('Delete')}>
                                <IconButton size="small" color="error" onClick={() => handleDelete(stream.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <Card variant="outlined">
            {loading && <LinearProgress color="secondary" />}
            <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={3}>
                    <Box flex={1}>
                        <Typography variant="h5" fontWeight={600} gutterBottom>
                            {t('Stream Library')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('Create reusable RTSP/HLS sources, bulk import from CSV, and push streams directly into your display walls.')}
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={openCreateDialog}
                            disabled={dialogBusy || importBusy}
                        >
                            {t('Add Stream')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FileUploadIcon />}
                            onClick={handleImportClick}
                            disabled={importBusy}
                        >
                            {importBusy ? t('Importing…') : t('Import CSV')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleBulkDelete}
                            disabled={!hasSelection || importBusy}
                        >
                            {t('Delete Selected')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<RefreshIcon />}
                            onClick={() => fetchStreams({ page: meta.current_page, search: meta.search || '' })}
                            disabled={loading}
                        >
                            {t('Refresh')}
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <TextField
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder={t('Search by name or URL')}
                        fullWidth
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {t('Total: :count streams', { ':count': meta.total ?? streams.length })}
                    </Typography>
                </Stack>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selectedIds.size > 0 && selectedIds.size < streams.length}
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        inputProps={{ 'aria-label': t('Select all streams') }}
                                    />
                                </TableCell>
                                <TableCell>{t('Stream')}</TableCell>
                                <TableCell>{t('Last used')}</TableCell>
                                <TableCell>{t('Updated')}</TableCell>
                                <TableCell align="right">{t('Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderTableBody()}
                        </TableBody>
                    </Table>
                </TableContainer>

                {loading && streams.length === 0 && (
                    <Box py={4} display="flex" justifyContent="center">
                        <CircularProgress size={32} />
                    </Box>
                )}

                {meta.last_page > 1 && (
                    <Box mt={3} display="flex" justifyContent="center">
                        <Pagination
                            color="primary"
                            count={meta.last_page}
                            page={meta.current_page}
                            onChange={handlePageChange}
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}

                {importSummary && (
                    <Box mt={3}>
                        <Alert severity="info" onClose={() => setImportSummary(null)}>
                            {t('Last import — Created: :created, Updated: :updated, Skipped: :skipped', {
                                ':created': importSummary.created ?? 0,
                                ':updated': importSummary.updated ?? 0,
                                ':skipped': importSummary.skipped ?? 0,
                            })}
                            {(importSummary.skipped_extra_columns ?? 0) > 0 && (
                                <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('Ignored due to extra columns: :count', { ':count': importSummary.skipped_extra_columns })}
                                </Typography>
                            )}
                        </Alert>
                    </Box>
                )}

                <input
                    type="file"
                    accept=".csv,text/csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </CardContent>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {dialogMode === 'create' ? t('Add Stream') : t('Edit Stream')}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label={t('Name')}
                            value={formValues.name}
                            onChange={handleFormChange('name')}
                            fullWidth
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name || t('Something memorable to identify this stream.')}
                            autoFocus
                        />
                        <TextField
                            label={t('RTSP / HLS URL')}
                            value={formValues.url}
                            onChange={handleFormChange('url')}
                            fullWidth
                            error={Boolean(formErrors.url)}
                            helperText={formErrors.url || t('Paste the full stream URL you want to reuse.')}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={dialogBusy}>
                        {t('Cancel')}
                    </Button>
                    <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={dialogBusy}>
                        {dialogBusy ? t('Saving…') : t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {feedback && (
                <Alert
                    severity={feedback.severity}
                    onClose={hideFeedback}
                    sx={{ position: 'fixed', bottom: 24, right: 24, minWidth: 280, zIndex: 1300 }}
                >
                    {feedback.message}
                </Alert>
            )}
        </Card>
    );
};

StreamManagementApp.propTypes = {
    initialStreams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        url: PropTypes.string,
        last_used_at: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
    })),
    initialMeta: PropTypes.shape({
        current_page: PropTypes.number,
        last_page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        search: PropTypes.string,
    }),
    endpoints: PropTypes.shape({
        list: PropTypes.string,
        create: PropTypes.string,
        update: PropTypes.string,
        delete: PropTypes.string,
        bulkDelete: PropTypes.string,
        import: PropTypes.string,
    }).isRequired,
    csrfToken: PropTypes.string,
};

export default StreamManagementApp;
